import { useCallback, useEffect, useRef } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type {
  CheckDefinition,
  CheckResult,
} from '@internal/backstage-plugin-pulse-common';
import {
  parseEntityRef,
  stringifyEntityRef,
  type Entity,
} from '@backstage/catalog-model';
import { checkResultsResponseSchema } from '../schemas/checks';
import {
  PULSE_CACHE_KEYS,
  PULSE_QUERY_CACHE_POLICIES,
  useCachedQuery,
} from './cache';
import { usePulseChecks } from './use-pulse-checks';

type CatalogApiLike = {
  getEntitiesByRefs(options: {
    entityRefs: string[];
  }): Promise<{ items: Array<Entity | undefined> }>;
};

const toCanonicalEntityRef = (entityRef: string): string =>
  stringifyEntityRef(
    parseEntityRef(entityRef, {
      defaultKind: 'component',
      defaultNamespace: 'default',
    }),
  );

export type CheckDetailsData = {
  check: CheckDefinition;
  results: Array<{
    entityRef: string;
    checkResult: CheckResult;
    entity?: Entity;
  }>;
  excludedEntities: Array<{
    id: string;
    entityRef: string;
    entity?: Entity;
  }>;
};

export const useCheckDetails = (checkId: string) => {
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef) as CatalogApiLike;
  const {
    value: checks,
    loading: checksLoading,
    refreshing: checksRefreshing,
    error: checksError,
    retry: retryChecks,
  } = usePulseChecks();
  const check = checks?.find(candidate => candidate.id === checkId);
  const refreshedMissingCheckRef = useRef<string>();
  const shouldRefreshMissingCheck = Boolean(
    checks && !check && refreshedMissingCheckRef.current !== checkId,
  );

  useEffect(() => {
    if (!shouldRefreshMissingCheck) {
      return;
    }

    refreshedMissingCheckRef.current = checkId;
    void retryChecks();
  }, [checkId, retryChecks, shouldRefreshMissingCheck]);

  const fetchCheckDetails = useCallback(async () => {
    try {
      if (!check) {
        throw new Error(`Check "${checkId}" not found`);
      }

      // Fetch check results for this specific check
      const resultsResponse = await fetchApi.fetch(
        `plugin://pulse/checks/${encodeURIComponent(checkId)}`,
      );
      if (!resultsResponse.ok) {
        throw new Error(
          `Failed to fetch check results: ${resultsResponse.status} ${resultsResponse.statusText}`,
        );
      }
      const resultsData = await resultsResponse.json();
      const parsedResults = checkResultsResponseSchema.parse(resultsData);

      // Extract entity refs from affected and excluded results
      const entityRefs = [
        ...parsedResults.results.map(result => result.entityRef),
        ...parsedResults.excludedEntityRefs,
      ];
      const normalizedEntityRefByResultRef = new Map(
        entityRefs.map(entityRef => [
          entityRef,
          toCanonicalEntityRef(entityRef),
        ]),
      );
      const canonicalEntityRefs = Array.from(
        new Set(normalizedEntityRefByResultRef.values()),
      );

      // Fetch entity details in batch
      let entities: Entity[] = [];
      if (canonicalEntityRefs.length > 0) {
        const catalogResponse: { items: Array<Entity | undefined> } =
          await catalogApi.getEntitiesByRefs({
            entityRefs: canonicalEntityRefs,
          });
        entities = catalogResponse.items.filter(
          (item: Entity | undefined): item is Entity => item !== undefined,
        );
      }

      const entityByRef = new Map(
        entities.map(entity => [stringifyEntityRef(entity), entity]),
      );

      // Merge results with entity details
      const mergedResults = parsedResults.results.map(result => ({
        ...result,
        entity: entityByRef.get(
          normalizedEntityRefByResultRef.get(result.entityRef) ??
            result.entityRef,
        ),
      }));

      const excludedEntities = parsedResults.excludedEntityRefs.map(
        entityRef => ({
          id: entityRef,
          entityRef,
          entity: entityByRef.get(
            normalizedEntityRefByResultRef.get(entityRef) ?? entityRef,
          ),
        }),
      );

      return {
        check,
        results: mergedResults,
        excludedEntities,
      };
    } catch (err) {
      throw err instanceof Error ? err : new Error(String(err));
    }
  }, [catalogApi, check, checkId, fetchApi]);

  const detailsQuery = useCachedQuery({
    cacheKey: PULSE_CACHE_KEYS.checkDetails(checkId),
    fetcher: fetchCheckDetails,
    ...PULSE_QUERY_CACHE_POLICIES.details,
    enabled: Boolean(check),
  });
  const value =
    detailsQuery.value && check
      ? { ...detailsQuery.value, check }
      : detailsQuery.value;

  return {
    ...detailsQuery,
    value,
    check,
    loading:
      checksLoading ||
      (!check && (checksRefreshing || shouldRefreshMissingCheck)) ||
      detailsQuery.loading,
    error: checksError ?? detailsQuery.error,
  };
};
